import React, { useMemo } from "react";

import { PostFeedCell } from "../PostFeedCell";
import * as DTO from "../../../../interfaces";
import { PostServices } from "@/services";

import { RequestChangesModal } from "../RequestChangesModal";

import "./styles.scss";
import { useSocialNetworks, getFullName } from "@/utils";
import { PostReviewEvent } from "@/interfaces";
import { ReviewHistoryModal } from "../ReviewHistoryModal";
import { VideoPageModal } from "@/containers";
import { usePostSearch } from "@/utils/usePostSearch";
import { Page, PostDetails } from "@/services/bedrock";

interface ReviewModalElement {
  postId: number;
  playerFullName: string;
  postSnUrls: string[];
}

interface OwnProps {
  postResponse?: Page<PostDetails>;
  players: { [key: string]: DTO.SubscribedPlayerSearchResponse };
}

export const PostFeed: React.FC<OwnProps> = ({ postResponse, players }) => {
  const postList = postResponse?.content || [];
  const [reviewModalElement, setReviewModalElements] = React.useState<ReviewModalElement | undefined>(undefined);
  const [historyEvents, setHistoryEvents] = React.useState<PostReviewEvent[] | undefined>(undefined);
  const [showHistoryModal, setShowHistoryModal] = React.useState<boolean>(false);
  const [videoPageId, setVideoPageId] = React.useState<number>();

  const { clearPostSearchCache } = usePostSearch();

  const { postableSocialNetworks } = useSocialNetworks();
  const changeReviewStatus = (postId: number, status: any, changesRequested?: string): void => {
    PostServices.setReviewStatus(postId, status, changesRequested)
      .catch(e => {
        alert(e.message);
      })
      .finally(() => {
        clearPostSearchCache();
      });
  };

  const videoPageModalInitialObject = useMemo(() => {
    if (videoPageId) return { videoPageId };
    return undefined;
  }, [videoPageId]);

  return (
    <div className="postFeedContainer">
      {postList.map(post => (
        <PostFeedCell
          key={post.id}
          player={players[post.playerId]?.playerAccount}
          post={post}
          activeSocialNetworks={postableSocialNetworks}
          onShowHistory={events => {
            setHistoryEvents(events);
            setShowHistoryModal(true);
          }}
          changeReviewStatus={(status, postSnUrls) => {
            if (status === "REVIEWED") {
              changeReviewStatus(post.id, status);
            } else if (status === "REQUEST_CHANGES") {
              setReviewModalElements({
                postId: post!.id,
                playerFullName: getFullName(
                  players[post.playerId]?.playerAccount?.firstName,
                  players[post.playerId]?.playerAccount?.lastName
                ),
                postSnUrls,
              });
            } else if (status === "COMPLETED") {
              changeReviewStatus(post.id, status);
            } else {
              clearPostSearchCache();
            }
          }}
          showVideoPageModal={setVideoPageId}
        />
      ))}

      <ReviewHistoryModal
        opened={showHistoryModal}
        title={"Compliance Review History"}
        events={historyEvents}
        closeHandler={() => setShowHistoryModal(false)}
      />

      {reviewModalElement && (
        <RequestChangesModal
          opened={true}
          title={"Enter the changes your Player must make to this post."}
          postId={reviewModalElement!.postId}
          playerName={reviewModalElement!.playerFullName}
          postLinks={reviewModalElement.postSnUrls || []}
          rejectHandler={() => {
            setReviewModalElements(undefined);
            clearPostSearchCache();
          }}
          acceptHandler={(requestedChanges, postId) => {
            setReviewModalElements(undefined);
            changeReviewStatus(postId, "REQUEST_CHANGES", requestedChanges);
          }}
        />
      )}

      <VideoPageModal
        open={!!videoPageModalInitialObject}
        initialObject={videoPageModalInitialObject}
        showStats
        onClose={() => setVideoPageId(undefined)}
      />
    </div>
  );
};
